import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchLoanById,
  updateLoanById,
  resetUpdateLoanState,
} from '../redux/slices/updateLoanSlice';
import '../css/LoanUpdate.css';
import { toast } from 'react-toastify';

const UpdateLoan = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loan, loading, error, success } = useSelector((state) => state.updateLoan || {});

  const [formData, setFormData] = useState({
    userId: '',
    userName: '',
    loanType: '',
    loanService: '',
    branchCode: '',
    loanRoiType: '',
    fixedLoanAmount: '',
    fixedLoanEmiAmount: '',
    fixedLoanTenure: '',
    fixedLoanFrequency: '',
    loanDisbursalMode: '',
    loanAppliedDate: '',
    loanInterest: '',
    loanPayable: '',
    loanPurpose: '',
    collectionLandmark: '',
    approvalDate: '',
    disbursalDate: '',
    status: '',
    kycStatus: '',
  });

  useEffect(() => {
    if (id) dispatch(fetchLoanById(id));
    return () => dispatch(resetUpdateLoanState());
  }, [dispatch, id]);

  useEffect(() => {
    if (loan) {
      setFormData({
        userId: loan.userId || '',
        userName: loan.userName || '',
        loanType: loan.loanType || '',
        loanService: loan.loanService || '',
        branchCode: loan.branchCode || '',
        loanRoiType: loan.loanRoiType || '',
        fixedLoanAmount: loan.fixedLoanAmount || '',
        fixedLoanEmiAmount: loan.fixedLoanEmiAmount || '',
        fixedLoanTenure: loan.fixedLoanTenure || '',
        fixedLoanFrequency: loan.fixedLoanFrequency || '',
        loanDisbursalMode: loan.loanDisbursalMode || '',
        loanAppliedDate: loan.loanAppliedDate ? loan.loanAppliedDate.slice(0, 10) : '',
        loanInterest: loan.loanInterest || '',
        loanPayable: loan.loanPayable || '',
        loanPurpose: loan.loanPurpose || '',
        collectionLandmark: loan.collectionLandmark || '',
        approvalDate: loan.approvalDate ? loan.approvalDate.slice(0, 10) : '',
        disbursalDate: loan.disbursalDate ? loan.disbursalDate.slice(0, 10) : '',
        status: loan.status || '',
        kycStatus: loan.kycStatus || '',
      });
    }
  }, [loan]);

  useEffect(() => {
    if (success) {
      toast.success('Loan updated successfully!');
      navigate('/LoanReport');
    }
  }, [success, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const pad = (n) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
      date.getHours()
    )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const updatedFormData = {
      ...formData,
      loanAppliedDate: formatDateTime(formData.loanAppliedDate),
      approvalDate: formatDateTime(formData.approvalDate),
      disbursalDate: formatDateTime(formData.disbursalDate),
    };

    dispatch(updateLoanById({ loanId: id, loanData: updatedFormData }));
  };

  if (loading && !loan) return <div className="LoanUpdate-loading">Loading loan data...</div>;
  if (error) return <div className="LoanUpdate-loading">Error: {error}</div>;

  const fields = [
    { label: 'Borrower Name', name: 'userName' },
    { label: 'Loan Type', name: 'loanType' },
    { label: 'Loan Service', name: 'loanService' },
    { label: 'Branch Code', name: 'branchCode' },
    { label: 'ROI Type', name: 'loanRoiType' },
    { label: 'Loan Amount', name: 'fixedLoanAmount' },
    { label: 'EMI Amount', name: 'fixedLoanEmiAmount' },
    { label: 'Tenure (Months)', name: 'fixedLoanTenure' },
    { label: 'Frequency', name: 'fixedLoanFrequency' },
    { label: 'Disbursal Mode', name: 'loanDisbursalMode' },
    { label: 'Applied Date', name: 'loanAppliedDate', type: 'date' },
    { label: 'Interest', name: 'loanInterest' },
    { label: 'Loan Payable', name: 'loanPayable' },
    { label: 'Purpose', name: 'loanPurpose' },
    { label: 'Landmark', name: 'collectionLandmark' },
    { label: 'Approval Date', name: 'approvalDate', type: 'date' },
    { label: 'Disbursal Date', name: 'disbursalDate', type: 'date' },
    { label: 'Status', name: 'status' },
    { label: 'KYC Status', name: 'kycStatus' },
  ];

  return (
    <div className="LoanUpdate-container">
      <h2 className="LoanUpdate-title">Update Loan</h2>
      <form className="LoanUpdate-form" onSubmit={handleSubmit}>
        <div className="LoanUpdate-grid">
          {fields.map(({ label, name, type = 'text' }) => (
            <div key={name} className="LoanUpdate-group">
              <label>{label}</label>
              <input
                type={type}
                name={name}
                value={formData[name]}
                onChange={handleChange}
                required
              />
            </div>
          ))}
        </div>
        <button type="submit" className="LoanUpdate-submitBtn">
          Update Loan
        </button>
      </form>
    </div>
  );
};

export default UpdateLoan;
